import {
  type AskPunttProjectData,
  type AskPunttProjectStage,
} from "@mg/schemas/src/christo/askPuntt";
import { FolderSimplePlus, Plus } from "@phosphor-icons/react";
import { Badge, Button, Flex, Text } from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";

import { askPunttChatRoute } from "./chatRoute";

import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import {
  useAskPunttChats,
  useCreateAskPunttChat,
} from "../../utils/queries/askPuntt";

type ProjectStatus = "complete" | "at_risk" | "delayed" | "in_progress";

function reviewStages(stages?: AskPunttProjectStage[]): AskPunttProjectStage[] {
  if (!stages) return [];
  return stages.filter((s) => s.name.toLowerCase() !== "final");
}

export function deriveProjectStatus(
  stages?: AskPunttProjectStage[],
): ProjectStatus {
  const visible = reviewStages(stages);
  if (visible.length === 0) return "in_progress";
  const hasBlocked = visible.some((s) => s.status === "blocked");
  if (hasBlocked) return "delayed";
  const hasNeedsReview = visible.some((s) => s.status === "needs_review");
  if (hasNeedsReview) return "at_risk";
  const allDone = visible.every((s) => s.status === "done");
  if (allDone) return "complete";
  const hasDone = visible.some((s) => s.status === "done");
  const restPending = visible.every(
    (s) => s.status === "done" || s.status === "pending",
  );
  if (hasDone && restPending) return "complete";
  return "in_progress";
}

const STATUS_CONFIG: Record<
  ProjectStatus,
  { label: string; color: "red" | "orange" | "green" | "gray" }
> = {
  delayed: { label: "Delayed", color: "red" },
  at_risk: { label: "At risk", color: "orange" },
  complete: { label: "Complete", color: "green" },
  in_progress: { label: "In progress", color: "gray" },
};

function formatCardDuration(ms: number): string {
  const days = Math.floor(Math.abs(ms) / (1000 * 60 * 60 * 24));
  if (days === 0) return "< 1 day";
  return `${days} day${days !== 1 ? "s" : ""}`;
}

function findReviewerForStage(
  stageName: string,
  routing?: AskPunttProjectData["routing"],
): string | null {
  if (!routing) return null;
  const lower = stageName.toLowerCase();
  const match = routing.find(
    (r) =>
      r.role?.toLowerCase().includes(lower) ||
      r.name?.toLowerCase().includes(lower),
  );
  return match?.name ?? null;
}

/** Unique reviewer (or stage) labels for multiple pending stages. */
export function formatPendingReviewersLine(
  stages: AskPunttProjectStage[],
  routing: AskPunttProjectData["routing"] | undefined,
  fallbackPrefix: "Pending" | "Blocked on",
): string {
  const labels = stages.map((s) => {
    const n = findReviewerForStage(s.name, routing);
    return n ?? s.name;
  });
  const unique = Array.from(new Set(labels));
  if (unique.length === 0) {
    return fallbackPrefix === "Blocked on" ? "Blocked" : "Pending review";
  }
  if (fallbackPrefix === "Blocked on") {
    if (unique.length === 1) return `Blocked on ${unique[0]}`;
    if (unique.length === 2) return `Blocked on ${unique[0]} & ${unique[1]}`;
    const last = unique[unique.length - 1];
    const rest = unique.slice(0, -1).join(", ");
    return `Blocked on ${rest} & ${last}`;
  }
  if (unique.length === 1) return `Pending ${unique[0]} review`;
  if (unique.length === 2) return `Pending ${unique[0]} & ${unique[1]} review`;
  const last = unique[unique.length - 1];
  const rest = unique.slice(0, -1).join(", ");
  return `Pending ${rest} & ${last} review`;
}

export function deriveBottomStatus(
  projectData?: AskPunttProjectData | null,
): string {
  if (!projectData) return "";
  const { targetDeadline, completedAt, routing } = projectData;
  const visible = reviewStages(projectData.stages);

  const deadlineMs = targetDeadline
    ? new Date(targetDeadline).getTime() - Date.now()
    : null;
  const timeContext =
    deadlineMs !== null
      ? deadlineMs > 0
        ? `${formatCardDuration(deadlineMs)} left`
        : `${formatCardDuration(Math.abs(deadlineMs))} overdue`
      : "";

  if (completedAt) {
    if (deadlineMs !== null && targetDeadline) {
      const completedMs = new Date(completedAt).getTime();
      const deadlineDate = new Date(targetDeadline).getTime();
      const delta = deadlineDate - completedMs;
      if (delta >= 0) {
        return `Completed ${formatCardDuration(delta)} early`;
      }
      return `Completed ${formatCardDuration(Math.abs(delta))} late`;
    }
    return "Completed";
  }

  if (visible.length === 0) return timeContext || "Just started";

  const needsReview = visible.filter((s) => s.status === "needs_review");
  if (needsReview.length > 0) {
    const label = formatPendingReviewersLine(needsReview, routing, "Pending");
    return timeContext ? `${label} · ${timeContext}` : label;
  }

  const blocked = visible.filter((s) => s.status === "blocked");
  if (blocked.length > 0) {
    const label = formatPendingReviewersLine(blocked, routing, "Blocked on");
    return timeContext ? `${label} · ${timeContext}` : label;
  }

  const allDone = visible.every(
    (s) => s.status === "done" || s.status === "pending",
  );
  const hasDone = visible.some((s) => s.status === "done");
  if (allDone && hasDone) {
    return timeContext ? `Complete · ${timeContext}` : "Complete";
  }

  const active = visible.filter((s) => s.status === "active");
  if (active.length > 0) {
    const label = formatPendingReviewersLine(active, routing, "Pending");
    return timeContext ? `${label} · ${timeContext}` : label;
  }

  return timeContext || "Setup in progress";
}

function miniStageBarBg(stage: AskPunttProjectStage): string {
  switch (stage.status) {
    case "done":
      return "bg-malachite-500";
    case "active":
      return "bg-puntt-gold-9 animate-pulse";
    case "needs_review":
      return "bg-puntt-red-9";
    case "blocked":
      return "bg-red-500";
    default:
      return "bg-puntt-neutral-gray-5";
  }
}

function miniStageLabelStyle(status: string): React.CSSProperties {
  if (status === "needs_review") {
    return {
      // backgroundColor: "rgb(var(--puntt-red-3))",
      color: "rgb(var(--puntt-red-11))",
      borderRadius: 3,
      padding: "0 4px",
      fontWeight: 600,
    };
  }
  if (status === "done") return { color: "rgb(var(--malachite-700))" };
  if (status === "active") return { color: "rgb(var(--puntt-gold-11))" };
  return {};
}

function MiniStageBar({ stages }: { stages: AskPunttProjectStage[] }) {
  const visible = reviewStages(stages);
  if (visible.length === 0) return null;
  return (
    <div className="mt-3">
      <Flex gap="0.5">
        {visible.map((stage) => (
          <div
            key={stage.name}
            className={`h-1.5 flex-1 rounded-full ${miniStageBarBg(stage)}`}
            title={`${stage.name}: ${stage.status}`}
          />
        ))}
      </Flex>
      <Flex gap="0.5" className="mt-0.5">
        {visible.map((stage) => (
          <Text
            key={stage.name}
            size="1"
            className="flex-1 text-center"
            style={{
              fontSize: "9px",
              lineHeight: "14px",
              ...miniStageLabelStyle(stage.status),
            }}
          >
            {stage.name}
          </Text>
        ))}
      </Flex>
    </div>
  );
}

function reviewerAvatarBg(status: string): string {
  switch (status) {
    case "done":
      return "bg-malachite-100 ring-1 ring-malachite-400";
    case "needs_review":
      return "bg-puntt-red-3 ring-1 ring-puntt-red-7";
    case "active":
      return "bg-puntt-gold-2 ring-1 ring-puntt-gold-5";
    default:
      return "bg-puntt-neutral-gray-3 ring-1 ring-puntt-neutral-gray-5";
  }
}

function reviewerAvatarText(status: string): string {
  switch (status) {
    case "done":
      return "text-malachite-700";
    case "needs_review":
      return "text-puntt-red-11";
    case "active":
      return "text-puntt-gold-11";
    default:
      return "text-puntt-neutral-gray-9";
  }
}

function ReviewerAvatars({
  routing,
}: {
  routing: AskPunttProjectData["routing"];
}) {
  if (!routing || routing.length === 0) return null;
  return (
    <Flex gap="1" align="center" className="mt-2.5">
      {routing.map((r) => (
        <div
          key={r.name}
          className={`flex size-6 items-center justify-center rounded-full ${reviewerAvatarBg(r.status)}`}
          title={`${r.name} · ${r.role} · ${r.status}`}
        >
          <span
            className={`text-[9px] font-bold leading-none ${reviewerAvatarText(r.status)}`}
          >
            {r.initials}
          </span>
        </div>
      ))}
    </Flex>
  );
}

function ProjectCard({
  chatId,
  name,
  projectData,
}: {
  chatId: string;
  name: string;
  projectData?: AskPunttProjectData | null;
}) {
  const navigate = useNavigate();
  const status = deriveProjectStatus(projectData?.stages);
  const config = STATUS_CONFIG[status];
  const markets = projectData?.markets?.join(" & ") || "";
  const projectType = projectData?.projectType || "";
  const reviewerCount = projectData?.routing?.length ?? 0;
  const bottomStatus = deriveBottomStatus(projectData);

  const descParts = [
    projectType,
    markets,
    reviewerCount > 0 ? `${reviewerCount} reviewers` : "",
  ].filter(Boolean);

  return (
    <button
      onClick={() =>
        navigate({
          to: askPunttChatRoute.to,
          params: { chatId },
        })
      }
      className="group flex flex-col justify-between rounded-2xl border border-puntt-neutral-gray-5 bg-base-white p-5 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-puntt-accent-7 hover:shadow-lg"
      data-testid="ask-puntt-project-card"
      data-chat-id={chatId}
    >
      <div>
        <Flex justify="between" align="start" gap="3" className="mb-3">
          <div className="min-w-0 flex-1 overflow-hidden">
            <Text
              as="div"
              size="4"
              weight="bold"
              className="w-full min-w-0 truncate text-puntt-neutral-gray-12"
            >
              {name}
            </Text>
          </div>
          <Badge
            size="1"
            color={config.color}
            variant="soft"
            className="shrink-0"
            data-testid="ask-puntt-project-status-badge"
          >
            {config.label}
          </Badge>
        </Flex>
        {descParts.length > 0 && (
          <Text size="2" className="text-puntt-neutral-gray-10">
            {descParts.join(" · ")}
          </Text>
        )}
        {projectData?.stages && projectData.stages.length > 0 && (
          <MiniStageBar stages={projectData.stages} />
        )}
        {projectData?.routing && projectData.routing.length > 0 && (
          <ReviewerAvatars routing={projectData.routing} />
        )}
      </div>
      {bottomStatus && (
        <Text
          size="1"
          className="mt-4 border-t border-puntt-neutral-gray-3 pt-3 text-puntt-neutral-gray-9"
        >
          {bottomStatus}
        </Text>
      )}
    </button>
  );
}

export default function AskPunttProjects() {
  const { data, isLoading } = useAskPunttChats();
  const navigate = useNavigate();
  const createChat = useCreateAskPunttChat();
  const analytics = useAnalytics("ask_puntt_projects");

  const projects = data?.chats?.filter((c) => c.projectData != null) ?? [];

  const handleNewProject = async () => {
    try {
      const result = await createChat.mutateAsync({
        message: "I want to set up a new project",
        workflowType: "project",
      });
      analytics.capture("ask_puntt_project_created_from_dashboard");
      navigate({
        to: askPunttChatRoute.to,
        params: { chatId: result.chat._id },
      });
    } catch (error) {
      analytics.capture("ask_puntt_project_create_error", {
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to create project:", error);
    }
  };

  return (
    <div
      className="ask-puntt-mesh-bg ask-puntt-noise flex h-full flex-col overflow-y-auto"
      data-testid="ask-puntt-projects"
    >
      <div className="mx-auto w-full max-w-[960px] px-6 py-8">
        <Flex justify="between" align="center" className="mb-6">
          <Text size="7" weight="bold" className="text-puntt-neutral-gray-12">
            Projects
          </Text>
          <Button
            size="2"
            variant="outline"
            className="cursor-pointer"
            disabled={createChat.isPending}
            onClick={handleNewProject}
            data-testid="ask-puntt-projects-new-btn"
          >
            <Plus size={14} weight="bold" />
            New project
          </Button>
        </Flex>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-40 animate-pulse rounded-2xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
              />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="4"
            className="mt-16"
          >
            <div className="flex size-16 items-center justify-center rounded-2xl bg-puntt-accent-2 ring-1 ring-puntt-accent-5/50">
              <FolderSimplePlus
                size={32}
                className="text-puntt-accent-9"
                weight="duotone"
              />
            </div>
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              No projects yet
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              Create your first project to set up reviewer routing, track
              stages, and get AI recommendations.
            </Text>
            <Button
              size="3"
              className="mt-2 cursor-pointer"
              disabled={createChat.isPending}
              onClick={handleNewProject}
              data-testid="ask-puntt-projects-empty-cta"
            >
              <FolderSimplePlus size={16} weight="bold" />
              New Project
            </Button>
          </Flex>
        ) : (
          <div
            className="grid grid-cols-2 gap-4"
            data-testid="ask-puntt-projects-grid"
          >
            {projects.map((chat) => (
              <ProjectCard
                key={chat._id}
                chatId={chat._id}
                name={chat.projectData?.name || chat.title}
                projectData={chat.projectData}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
